import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { X } from 'lucide-react';
import { EMOTION_OPTIONS, SETUP_OPTIONS } from './journalConstants';

const Field = ({ label, children }) => (
  <div>
    <label className="block text-xs font-semibold text-gray-600 mb-1">{label}</label>
    {children}
  </div>
);

/**
 * Trade entry form. Controlled — parent owns `form` state and onChange.
 */
const TradeForm = ({ form, editingId, loading, onChange, onSubmit, onCancel }) => (
  <Card data-testid="trade-form">
    <CardHeader>
      <div className="flex items-center justify-between">
        <CardTitle>{editingId ? 'Edit Trade' : 'Log New Trade'}</CardTitle>
        <Button variant="ghost" size="sm" onClick={onCancel}>
          <X size={16} />
        </Button>
      </div>
    </CardHeader>
    <CardContent>
      <form className="space-y-6" onSubmit={onSubmit}>
        <div className="grid md:grid-cols-4 gap-4">
          <Field label="Symbol *">
            <Input name="symbol" value={form.symbol} onChange={onChange} placeholder="AAPL" />
          </Field>
          <Field label="Side">
            <select name="side" value={form.side} onChange={onChange} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm">
              <option value="buy">Buy (Long)</option>
              <option value="sell">Sell</option>
              <option value="short">Short</option>
            </select>
          </Field>
          <Field label="Trade Type">
            <select name="tradeType" value={form.tradeType} onChange={onChange} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm">
              <option value="stock">Stock</option>
              <option value="option">Option</option>
              <option value="futures">Futures</option>
              <option value="crypto">Crypto</option>
              <option value="forex">Forex</option>
            </select>
          </Field>
          <Field label="Status">
            <select name="status" value={form.status} onChange={onChange} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm">
              <option value="open">Open</option>
              <option value="closed">Closed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </Field>
        </div>

        <div className="grid md:grid-cols-4 gap-4">
          <Field label="Entry Price">
            <Input name="entryPrice" type="number" step="0.01" value={form.entryPrice} onChange={onChange} placeholder="150.25" />
          </Field>
          <Field label="Entry Date">
            <Input name="entryDate" type="date" value={form.entryDate} onChange={onChange} />
          </Field>
          <Field label="Exit Price">
            <Input name="exitPrice" type="number" step="0.01" value={form.exitPrice} onChange={onChange} placeholder="155.00" />
          </Field>
          <Field label="Exit Date">
            <Input name="exitDate" type="date" value={form.exitDate} onChange={onChange} />
          </Field>
        </div>

        <div className="grid md:grid-cols-4 gap-4">
          <Field label="Position Size">
            <Input name="size" type="number" value={form.size} onChange={onChange} placeholder="100" />
          </Field>
          <Field label="Stop Loss">
            <Input name="stopLoss" type="number" step="0.01" value={form.stopLoss} onChange={onChange} placeholder="145.00" />
          </Field>
          <Field label="Take Profit">
            <Input name="takeProfit" type="number" step="0.01" value={form.takeProfit} onChange={onChange} placeholder="160.00" />
          </Field>
          <Field label="Fees">
            <Input name="fees" type="number" step="0.01" value={form.fees} onChange={onChange} placeholder="2.50" />
          </Field>
        </div>

        <div className="grid md:grid-cols-4 gap-4">
          <Field label="Strategy">
            <Input name="strategy" value={form.strategy} onChange={onChange} placeholder="Momentum" />
          </Field>
          <Field label="Setup">
            <select name="setup" value={form.setup} onChange={onChange} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm">
              <option value="">Select setup...</option>
              {SETUP_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Confidence (1-100)">
            <Input name="confidence" type="number" min="1" max="100" value={form.confidence} onChange={onChange} placeholder="75" />
          </Field>
          <Field label="Emotions">
            <select name="emotions" value={form.emotions} onChange={onChange} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm">
              {EMOTION_OPTIONS.map((e) => <option key={e.value} value={e.value}>{e.label}</option>)}
            </select>
          </Field>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Trade Thesis">
            <Textarea name="thesis" value={form.thesis} onChange={onChange} rows={3} placeholder="Why are you taking this trade?" />
          </Field>
          <Field label="Notes">
            <Textarea name="notes" value={form.notes} onChange={onChange} rows={3} placeholder="Additional observations..." />
          </Field>
        </div>

        {form.status === 'closed' && (
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Mistakes Made">
              <Textarea name="mistakes" value={form.mistakes} onChange={onChange} rows={2} placeholder="What went wrong?" />
            </Field>
            <Field label="Lessons Learned">
              <Textarea name="lessons" value={form.lessons} onChange={onChange} rows={2} placeholder="What did you learn?" />
            </Field>
          </div>
        )}

        <Field label="Tags (comma-separated)">
          <Input name="tags" value={form.tags} onChange={onChange} placeholder="earnings, breakout, tech" />
        </Field>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
          <Button type="submit" disabled={loading} className="bg-purple-600 hover:bg-purple-700" data-testid="trade-form-submit">
            {editingId ? 'Update Trade' : 'Log Trade'}
          </Button>
        </div>
      </form>
    </CardContent>
  </Card>
);

export default TradeForm;
